import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCloudAuth, loggingOutRef } from "../../hooks/useCloudAuth";
import { getLoginUrl } from "../../lib/api/cloud";
import { PendingApproval } from "../../pages/dashboard/PendingApproval";

interface KeycloakAuthGateProps {
  children: ReactNode;
}

/**
 * Top-level auth gate for closed alpha.
 * - If not authenticated → auto-redirect to Keycloak login
 * - If authenticated but pending approval → show PendingApproval page
 * - If authenticated and approved → render children
 * - Banned users see a banned message
 *
 * Supports ?redirect= query parameter for cross-subdomain auth:
 * instances at *.cc.somebugs.com can redirect unauthenticated users
 * to the control plane, which will forward them back after login.
 */
export function KeycloakAuthGate({ children }: KeycloakAuthGateProps) {
  const { t } = useTranslation();
  const { user, loading, isAuthenticated, logout } = useCloudAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [redirecting, setRedirecting] = useState(false);

  // If a ?redirect= query param is present (e.g., from an instance),
  // use it as the post-login destination; otherwise fall back to the current path.
  const externalRedirect = searchParams.get("redirect");
  // When Keycloak redirects back after SSO session termination, this flag
  // prevents the auth gate from immediately re-authenticating the user.
  const loggedOut = searchParams.get("logged_out") === "1";

  useEffect(() => {
    if (loading || isAuthenticated || redirecting || loggedOut || loggingOutRef.current) return;

    // Not authenticated — redirect to Keycloak login
    setRedirecting(true);
    const destination = externalRedirect || location.pathname + location.search;
    getLoginUrl(destination)
      .then(({ url }) => {
        window.location.href = url;
      })
      .catch(() => {
        setRedirecting(false);
      });
  }, [loading, isAuthenticated, redirecting, loggedOut, externalRedirect, location.pathname, location.search]);

  // Show spinner while loading auth state or redirecting to Keycloak
  if (loading || (!isAuthenticated && redirecting)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-bg">
        <div className="text-center">
          <div className="w-6 h-6 mx-auto mb-3 border-2 border-warm-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-warm-500 text-sm">
            {redirecting ? t('auth.redirectingToSignIn') : t('auth.loading')}
          </p>
        </div>
      </div>
    );
  }

  // User was just logged out via Keycloak SSO — show confirmation instead of
  // immediately re-redirecting to login.
  if (!isAuthenticated && loggedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-bg px-4">
        <div className="w-full max-w-sm text-center">
          <h1 className="text-2xl font-bold text-warm-50 mb-2">{t('auth.title')}</h1>
          <p className="text-warm-500 text-sm mb-8">{t('auth.signedOut')}</p>
          <button
            onClick={() => {
              setRedirecting(true);
              getLoginUrl("/")
                .then(({ url }) => {
                  window.location.href = url;
                })
                .catch(() => {
                  setRedirecting(false);
                });
            }}
            className="w-full py-2.5 rounded-lg font-medium text-sm transition-all
                       bg-gradient-to-r from-blue-500 to-purple-600
                       hover:from-blue-400 hover:to-purple-500
                       text-white"
          >
            {t('auth.signIn')}
          </button>
        </div>
      </div>
    );
  }

  // Not authenticated and not redirecting (redirect failed) — show login button fallback
  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-bg px-4">
        <div className="w-full max-w-sm text-center">
          <h1 className="text-2xl font-bold text-warm-50 mb-2">{t('auth.title')}</h1>
          <p className="text-warm-500 text-sm mb-8">{t('auth.subtitle')}</p>
          <button
            onClick={() => {
              setRedirecting(true);
              const destination = externalRedirect || location.pathname + location.search;
              getLoginUrl(destination)
                .then(({ url }) => {
                  window.location.href = url;
                })
                .catch(() => {
                  setRedirecting(false);
                });
            }}
            className="w-full py-2.5 rounded-lg font-medium text-sm transition-all
                       bg-gradient-to-r from-blue-500 to-purple-600
                       hover:from-blue-400 hover:to-purple-500
                       text-white"
          >
            {t('auth.signIn')}
          </button>
        </div>
      </div>
    );
  }

  // Authenticated but pending approval
  if (user.status === "pending") {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <PendingApproval />
      </div>
    );
  }

  // Authenticated but banned
  if (user.status === "banned") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] px-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-[#f5f5f5] mb-2">{t('auth.banned.title')}</h1>
          <p className="text-[#999] mb-4">
            {t('auth.banned.message')}
          </p>
          <button
            onClick={logout}
            className="text-[#999] text-sm hover:text-[#f5f5f5] transition-colors underline underline-offset-2"
          >
            {t('auth.signOut')}
          </button>
        </div>
      </div>
    );
  }

  // Authenticated and approved (or suspended — let inner components handle that)
  return <>{children}</>;
}
